import * as React from 'react';
import * as styles from '../../help/help.module.scss';
import HelpDocument from '../../../models/helpDocument';
import StatusChip from './status-chip';
import { IconButton } from '@material-ui/core';
import { Edit, Delete, MoreVert, Archive, Navigation } from '@material-ui/icons';
import ConfirmationModal from '../../report/customViewModal/confirmationModal';
import ConfirmationDialog from '../../common/confirmationDialog';
import useHelpDocuments from '../../../hooks/useHelpDocuments';
import { useParams, useHistory } from 'react-router-dom';
import Paths from '../../../models/enums/paths';
import { TreeData } from '@atlaskit/tree';
import DropdownMenu from '../../common/dropdown-menu';
import useModal from '../../../hooks/useModal';
import ModalDialog from '../../modal-dialog/modalDialog';
import GlobalMapModalContent from './global-map-modal-content';
import Client from '../../../models/client';
import ShadowDOMContent from './shadow-dom-content';

interface Props {
  document: HelpDocument;
  activeDoc: number;
  isSearching: boolean;
}

/**
 * Component that displays each individual help document
 */
const HelpDocumentContent = ({ document, activeDoc, isSearching }: Props) => {
  const { clientId } = useParams<{ clientId: string }>();
  const docRef = React.useRef<HTMLDivElement | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const { deleteGlobalHelpDocument, deleteClientHelpDocument, clientList } = useHelpDocuments();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { modalIsOpen: mapModalIsOpen, openModal: openMapModal, closeModal: closeMapModal } = useModal();

  // If the active document changes, the window will scroll to the appropriate document
  React.useEffect(() => {
    // Set timeout is necessary in order to fully scroll to the document, otherwise, the images that are part of that document might not be fully rendered.
    const timer = setTimeout(() => {
      if (activeDoc === document.snippetGroupId) {
        docRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [activeDoc]);

  const isGlobal = document.clientId === -1;

  const deleteDocument = () => {
    if (!clientId) {
      deleteGlobalHelpDocument(document.snippetId).then((newDocumentTree: TreeData) => {
        if (document.snippetGroupId === activeDoc) {
          history.push(
            `${Paths.GlobalHelpDocuments}/${newDocumentTree.items['-1'].children[0] || ''}`.replace(
              ':clientId',
              clientId
            )
          );
        }
      });
    } else {
      deleteClientHelpDocument(document.snippetId).then((newDocumentTree: TreeData) => {
        if (document.snippetGroupId === activeDoc) {
          history.push(
            `${Paths.ClientHelpDocuments}/${newDocumentTree.items['-1'].children[0] || ''} `.replace(
              ':clientId',
              clientId
            )
          );
        }
      });
    }
    setModalOpen(false);
  };

  const editDocument = () => {
    if (!isGlobal && clientId) {
      history.push(
        Paths.EditClientHelpDocument.replace(':clientId', clientId).replace(':snippetId', document.snippetId.toString())
      );
    } else if (isGlobal && clientId) {
      history.push(
        `${Paths.EditClientHelpDocument.replace(':clientId', clientId).replace(
          ':snippetId',
          document.snippetId.toString()
        )}/global`
      );
    } else if (isGlobal && !clientId) {
      history.push(Paths.EditGlobalHelpDocument.replace(':snippetId', document.snippetId.toString()));
    }
  };

  const viewRevisionsList = () => {
    if (isGlobal) {
      history.push(
        Paths.ViewGlobalHelpDocumentRevisions.replace(':snippetGroupId', document.snippetGroupId.toString())
      );
    } else {
      history.push(
        Paths.ViewClientHelpDocumentRevisions.replace(':clientId', clientId).replace(
          ':snippetGroupId',
          document.snippetGroupId.toString()
        )
      );
    }
  };

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const menuItems = [
    { type: 'revisions', text: 'Revisions', eventHandler: viewRevisionsList, icon: Archive },
    { type: 'mapping', text: 'Map to Clients', eventHandler: openMapModal, icon: Navigation }
  ];

  const mappedClients = clientList.filter((client: Client) => document.mappedClient.includes(client.clientId));
  const unmappedClients = clientList.filter((client: Client) => !document.mappedClient.includes(client.clientId));

  return (
    <div className={styles.document} ref={docRef}>
      <ConfirmationModal
        open={modalOpen}
        closeModal={() => {}}
        children={
          <ConfirmationDialog
            title="Confirmation"
            message="Are you sure you want to delete this document? Deleting the document also deletes all of its revisions."
            confirmAction={deleteDocument}
            cancelAction={() => setModalOpen(false)}
          />
        }
      />
      {isGlobal && (
        <ModalDialog
          isOpen={mapModalIsOpen}
          onClose={closeMapModal}
          title={!clientId && 'Map Document to Clients'}
          width="650px"
          children={
            !clientId && (
              <GlobalMapModalContent
                initialMappedClients={mappedClients}
                initialUnmappedClients={unmappedClients}
                documentId={document.snippetGroupId}
                closeMapModal={closeMapModal}
              />
            )
          }
        />
      )}
      <div className={styles.documentTools}>
        <div className={styles.statusIndicators}>
          <StatusChip text={isGlobal ? 'Global' : 'Client'} backgroundColor={styles.statusGlobal} />
          <StatusChip text={document.isPublished ? 'Published' : 'Draft'} backgroundColor={styles.statusPublished} />
          <StatusChip text={`Rev. #${document.revision}`} backgroundColor={styles.statusPublished} />
        </div>
        {!isSearching && (
          <div className={styles.documentActions}>
            <IconButton title="Edit" size="small" onClick={editDocument}>
              <Edit style={{ fill: '#006DA8' }} />
            </IconButton>
            {((!isGlobal && clientId) || (isGlobal && !clientId)) && (
              <IconButton title="Delete" size="small" onClick={() => setModalOpen(true)}>
                <Delete style={{ fill: '#006DA8' }} />
              </IconButton>
            )}
            {((!isGlobal && clientId) || (isGlobal && !clientId)) && (
              <React.Fragment>
                <IconButton title="Menu" size="small" onClick={openMenu}>
                  <MoreVert style={{ fill: '#006DA8' }} />
                </IconButton>
                <DropdownMenu
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  menuItems={isGlobal ? menuItems : menuItems.filter((item) => item.type !== 'mapping')}
                />
              </React.Fragment>
            )}
          </div>
        )}
      </div>
      <ShadowDOMContent document={document} />
    </div>
  );
};

export default HelpDocumentContent;
